import React, { useContext, useEffect, useState } from 'react';
import AuthContext from '../components/AuthContext';
import { postRequest } from '../api/ApiController';
import { LocaleContext } from '../components/LocaleContext';
import UtilContext from '../components/UtilContext';

function ProfilePage() {
  const { user } = useContext(AuthContext);
  const { t } = useContext(LocaleContext);
  const { setDialogText, setVisible, toast } = useContext(UtilContext);
  const parseLanguageToken = (value) => {
    const lang = String(value ?? '').trim().toLowerCase();
    return ['sk', 'en', 'de'].includes(lang) ? lang : '';
  };
  const normalizeLanguage = (value) => parseLanguageToken(value) || 'sk';
  /** Účet v DB/JWT má prednosť pred cache v localStorage (inak zostane starý EN po zmene v Používatelia / navbare). */
  const resolveAccountPreferredLanguage = (u) => {
    const authUser = parseLanguageToken(u?.preferred_language);
    if (authUser) return authUser;
    const authCompany = parseLanguageToken(u?.company_preferred_language);
    if (authCompany) return authCompany;
    const lsUser = parseLanguageToken(localStorage.getItem('preferred_language_user'));
    if (lsUser) return lsUser;
    const lsCompany = parseLanguageToken(localStorage.getItem('preferred_language_company'));
    if (lsCompany) return lsCompany;
    return 'sk';
  };
  const [showQrCode, setShowQrCode] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secretCode, setSecretCode] = useState('');
  const [otp, setOtp] = useState(user?.otp_enabled);
  const [preferredLanguage, setPreferredLanguage] = useState(() => resolveAccountPreferredLanguage(user));
  const [savedBaseline, setSavedBaseline] = useState(() => resolveAccountPreferredLanguage(user));
  const [savingLanguage, setSavingLanguage] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    const next = resolveAccountPreferredLanguage(user);
    setPreferredLanguage(next);
    setSavedBaseline(next);
  }, [user?.id, user?.preferred_language, user?.company_preferred_language]);

  const preferredLanguageChanged = (preferredLanguage || '') !== (savedBaseline || '');

  const disableOTP = () => {
    postRequest('/api/auth/disable2FA', [])
      .then((response) => {
        if (response.status < 400) {
          setShowQrCode(false);
          setOtp('disabled');
          if (user) user.otp_enabled = 'disabled';
        }
      })
      .catch(() => {});
  };

  const enableOTP = () => {
    postRequest('/api/auth/enable2FA', [])
      .then((response) => {
        if (response.status < 400) {
          const lines = response.data.QR_Code.split('\n');
          lines.splice(0, 1);
          const newtext = lines.join('\n');
          setQrCode(newtext);
          setSecretCode(response.data.google2fa_secret);
          setShowQrCode(true);
          setOtp('enabled');
          if (user) user.otp_enabled = 'enabled';
        }
      })
      .catch(() => {});
  };

  const savePreferredLanguage = async () => {
    setSavingLanguage(true);
    setDialogText?.(t('Common.Updating'));
    setVisible?.(true);
    const formData = new FormData();
    formData.append('preferred_language', preferredLanguage || 'sk');
    try {
      const response = await postRequest('/api/me/preferred-language', formData, true);
      if (response?.status === 200) {
        const nextUserPref = response?.data?.preferred_language || '';
        const nextCompanyPref = response?.data?.company_preferred_language || '';
        if (nextUserPref) localStorage.setItem('preferred_language_user', nextUserPref);
        else localStorage.removeItem('preferred_language_user');
        if (nextCompanyPref) localStorage.setItem('preferred_language_company', nextCompanyPref);
        else localStorage.removeItem('preferred_language_company');
        const effectiveLocale = normalizeLanguage(nextUserPref || nextCompanyPref || 'sk');
        setPreferredLanguage(effectiveLocale);
        setSavedBaseline(effectiveLocale);
        window.dispatchEvent(new Event('app-locale-preferences-updated'));
        // Do not call setUser here; it triggers full init/startRequest refresh.
        if (user) user.preferred_language = nextUserPref || null;
        toast?.current?.show({
          severity: 'success',
          summary: t('ServiceTask.Confirmed'),
          detail: t('ProfilePage.Toast.PreferredLanguageSaved'),
          life: 3000,
        });
      } else {
        toast?.current?.show({
          severity: 'error',
          summary: t('Common.Error'),
          detail: response?.message || t('ProfilePage.Toast.PreferredLanguageSaveFailed'),
          life: 4500,
        });
      }
    } catch (e) {
      toast?.current?.show({
        severity: 'error',
        summary: t('Common.Error'),
        detail: e?.message || t('ProfilePage.Toast.PreferredLanguageSaveFailed'),
        life: 4500,
      });
    } finally {
      setVisible?.(false);
      setSavingLanguage(false);
    }
  };

  return (
    <div className="flex flex-col bg-gray-100 rounded-md shadow-md px-4 py-4 h-[calc(100vh_-_137px)] relative">
      <div>
        <h2 className="text-xl font-bold px-1 py-1 mb-2">{t('ProfilePage.Title')}</h2>
      </div>
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full py-2">
          <div className="overflow-hidden">
            <div className="md:grid grid-cols-1 gap-4 mb-2">
              <div>
                <label htmlFor="preferred_language" className="block text-md font-semibold text-gray-800 mb-2">
                  {t('ProfilePage.PreferredLanguage')}
                </label>
                <p className="text-sm text-gray-600 mb-2">{t('ProfilePage.PreferredLanguageHint')}</p>
                <div className="flex items-center gap-2">
                  <select
                    id="preferred_language"
                    value={preferredLanguage}
                    onChange={(e) => setPreferredLanguage(e.target.value)}
                    className="h-10 px-3 border border-gray-300 rounded-md bg-white"
                  >
                    <option value="sk">Slovensky (SK)</option>
                    <option value="en">English (EN)</option>
                    <option value="de">Deutsch (DE)</option>
                  </select>
                  <button
                    type="button"
                    onClick={savePreferredLanguage}
                    disabled={savingLanguage || !preferredLanguageChanged}
                    className="px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-60"
                  >
                    {t('Common.Save')}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="otp" className="block text-md font-semibold text-gray-800">
                  {t('ProfilePage.TwoFactor')}: {otp !== undefined ? String(otp).toUpperCase() : null}
                </label>
                {otp === 'enabled' ? (
                  <button onClick={() => disableOTP()} className="px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600">
                    {t('ProfilePage.Disable')}
                  </button>
                ) : (
                  <button onClick={() => enableOTP()} className="px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600">
                    {t('ProfilePage.Enable')}
                  </button>
                )}
              </div>
              {showQrCode && (
                <div className="relative flex flex-col items-center min-h-min overflow-hidden">
                  <h1 className="text-center font-semibold text-xl">{t('ProfilePage.GoogleAuthenticatorSetup')}</h1>
                  <p>{t('ProfilePage.GoogleAuthenticatorScanHint')}</p>
                  <div>
                    <img src={`data:image/svg+xml;utf8,${encodeURIComponent(qrCode)}`} alt="qr" />
                  </div>
                  <p>{t('ProfilePage.OrUseSetupKey')}</p>
                  <p className="font-bold mb-4">{secretCode}</p>
                  <p>{t('ProfilePage.GoogleAuthenticatorRequired')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;